import {
  Alert,
  Badge,
  Button,
  Card,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { AdminApiError, adminGet, adminSend } from "../../api.ts";
import { AdminTable } from "../../components/AdminTable.tsx";
import { TablePagination } from "../../components/TablePagination.tsx";
import { useClientPagination } from "../../hooks/useClientPagination.ts";

/** Date -> datetime-local input value (本地时区 yyyy-MM-ddThh:mm) */
const toLocalInput = (d: Date | null): string => {
  if (d === null) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
};
const fromLocalInput = (s: string): Date | null => {
  if (s === "") return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};

interface InstanceSummary {
  readonly id: string;
  readonly instanceId: string;
  readonly enabled: boolean;
  readonly siteCode: string;
  readonly siteName?: string;
}

interface BotDetail {
  readonly id: string;
  readonly paymentInstanceId: string | null;
  readonly inviteSiteUrl: string | null;
  readonly activityStartAt: string | null;
  readonly activityEndAt: string | null;
}

interface FormValues {
  paymentInstanceId: string | null;
  inviteSiteUrl: string;
  activityStartAt: Date | null;
  activityEndAt: Date | null;
}

interface PaymentBinding {
  readonly tgUserId: number;
  readonly platformAccount: string;
  readonly childSiteCode: string;
  readonly paymentInstanceId: string;
  readonly platformUserIdx: number;
  readonly boundAt: string;
}

const fetchInstances = () => adminGet<ReadonlyArray<InstanceSummary>>("/payment/instances");
const fetchBot = (id: string) => adminGet<BotDetail>(`/bots/${id}`);
const fetchBindings = (botId: string) =>
  adminGet<{ items: ReadonlyArray<PaymentBinding> }>(`/bots/${botId}/payment-bindings`);

const patchBot = (id: string, body: Record<string, unknown>) =>
  adminSend<{ ok: true }>("PATCH", `/bots/${id}`, body);

export function PaymentTab({ botId }: { readonly botId: string }) {
  const qc = useQueryClient();
  const instancesQuery = useQuery({
    queryKey: ["payment-instances"],
    queryFn: fetchInstances,
  });
  const botQuery = useQuery({
    queryKey: ["bot", botId, "detail"],
    queryFn: () => fetchBot(botId),
  });
  const bindingsQuery = useQuery({
    queryKey: ["bot", botId, "payment-bindings"],
    queryFn: () => fetchBindings(botId),
  });

  const form = useForm<FormValues>({
    initialValues: {
      paymentInstanceId: null,
      inviteSiteUrl: "",
      activityStartAt: null,
      activityEndAt: null,
    },
    validate: {
      activityEndAt: (v, values) =>
        v && values.activityStartAt && v <= values.activityStartAt
          ? "结束时间必须晚于起始时间"
          : null,
    },
  });

  // 初次拉取后填表
  const [filled, setFilled] = useState(false);
  useEffect(() => {
    if (botQuery.data && !filled) {
      form.setValues({
        paymentInstanceId: botQuery.data.paymentInstanceId,
        inviteSiteUrl: botQuery.data.inviteSiteUrl ?? "",
        activityStartAt: botQuery.data.activityStartAt
          ? new Date(botQuery.data.activityStartAt)
          : null,
        activityEndAt: botQuery.data.activityEndAt ? new Date(botQuery.data.activityEndAt) : null,
      });
      setFilled(true);
    }
  }, [botQuery.data, filled, form]);

  const save = useMutation({
    mutationFn: (vals: FormValues) =>
      patchBot(botId, {
        paymentInstanceId: vals.paymentInstanceId,
        inviteSiteUrl: vals.inviteSiteUrl.trim() === "" ? null : vals.inviteSiteUrl.trim(),
        activityStartAt: vals.activityStartAt ? vals.activityStartAt.toISOString() : null,
        activityEndAt: vals.activityEndAt ? vals.activityEndAt.toISOString() : null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["bot", botId, "detail"] });
      notifications.show({ message: "已保存", color: "green" });
    },
    onError: (e: AdminApiError) => notifications.show({ message: e.message, color: "red" }),
  });

  const unbind = useMutation({
    mutationFn: (tgUserId: number) =>
      adminSend<{ ok: true }>("DELETE", `/bots/${botId}/payment-bindings/${tgUserId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["bot", botId, "payment-bindings"] });
      notifications.show({ message: "已解绑", color: "green" });
    },
    onError: (e: AdminApiError) => notifications.show({ message: e.message, color: "red" }),
  });

  const confirmUnbind = (b: PaymentBinding) =>
    modals.openConfirmModal({
      title: "解绑用户支付账号",
      children: (
        <Text size="sm">
          确认解绑 TG 用户 {b.tgUserId}（平台账号 {b.platformAccount}
          ）？解绑后该用户可重新绑定其他账号。
        </Text>
      ),
      labels: { confirm: "解绑", cancel: "取消" },
      confirmProps: { color: "red" },
      onConfirm: () => unbind.mutate(b.tgUserId),
    });

  const [bindingSearch, setBindingSearch] = useState("");
  const bindingItems = bindingsQuery.data?.items ?? [];
  const bindingKeyword = bindingSearch.trim().toLowerCase();
  const filteredBindings = useMemo(() => {
    if (bindingKeyword === "") return bindingItems;
    return bindingItems.filter((b) => {
      const tg = String(b.tgUserId);
      const account = b.platformAccount.toLowerCase();
      const site = b.childSiteCode.toLowerCase();
      return (
        tg.includes(bindingKeyword) ||
        account.includes(bindingKeyword) ||
        site.includes(bindingKeyword)
      );
    });
  }, [bindingItems, bindingKeyword]);
  const {
    pageItems: pagedBindings,
    page: bindingsPage,
    setPage: setBindingsPage,
    pageSize: bindingsPageSize,
    total: bindingsTotal,
    totalPages: bindingsTotalPages,
  } = useClientPagination(filteredBindings, bindingKeyword);

  if (instancesQuery.isLoading || botQuery.isLoading) {
    return <Text>加载中...</Text>;
  }

  const instanceOptions =
    instancesQuery.data
      ?.filter((i) => i.enabled)
      .map((i) => ({
        value: i.id,
        label: `${i.siteCode}${i.siteName ? ` - ${i.siteName}` : ""} (${i.instanceId})`,
      })) ?? [];

  return (
    <Stack gap="md">
      <Title order={3}>支付集成</Title>
      <Card withBorder>
        <form onSubmit={form.onSubmit((vals) => save.mutate(vals))}>
          <Stack gap="md">
            <Select
              label="Payment Instance"
              placeholder="选一个已 onboard 的实例（格式: siteCode - siteName (instanceId)）"
              data={instanceOptions}
              clearable
              {...form.getInputProps("paymentInstanceId")}
            />
            <TextInput
              label="邀请站 URL"
              placeholder="WG 玩家产品站, 如 https://5dvip.vip"
              description="/invite 命令与 Mini App 邀请页据此 + 用户 WG 会员 ID 生成拉新注册链接"
              {...form.getInputProps("inviteSiteUrl")}
            />
            <TextInput
              type="datetime-local"
              label="活动起始时间 (不填 = 不限)"
              value={toLocalInput(form.values.activityStartAt)}
              onChange={(e) =>
                form.setFieldValue("activityStartAt", fromLocalInput(e.currentTarget.value))
              }
            />
            <TextInput
              type="datetime-local"
              label="活动结束时间 (不填 = 不限)"
              value={toLocalInput(form.values.activityEndAt)}
              onChange={(e) =>
                form.setFieldValue("activityEndAt", fromLocalInput(e.currentTarget.value))
              }
              error={form.errors["activityEndAt"]}
            />
            {form.values.paymentInstanceId === null && (
              <Alert color="yellow" variant="light">
                未配置 payment_instance 时, 该 bot 不会自动发券 / 派彩.
              </Alert>
            )}
            <Button type="submit" loading={save.isPending}>
              保存
            </Button>
          </Stack>
        </form>
      </Card>
      <Card withBorder>
        <Stack gap="xs">
          <Title order={5}>说明</Title>
          <Text size="sm" c="dimmed">
            一个 bot 对应一个 WG instance + 一个子站. KOL 多个 bot 可共指同一 instance.
          </Text>
          <Text size="sm" c="dimmed">
            活动时间窗仅影响"个人存款发券"的扫描起点; 邀请首存判定走 lifetime, 不受窗口限制.
          </Text>
          <Text size="sm" c="dimmed">
            邀请奖励: 邀请人需在 Mini App 手动填报下级会员 ID, 系统验证 WG 上级关系后,
            下级在邀请人绑定时间之后首存即发券。
          </Text>
        </Stack>
      </Card>
      <Card withBorder>
        <Stack gap="md">
          <Title order={5}>用户支付绑定列表</Title>
          {bindingsQuery.isLoading ? (
            <Stack gap="xs">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} h={36} radius="sm" />
              ))}
            </Stack>
          ) : bindingItems.length === 0 ? (
            <Text size="sm" c="dimmed">
              暂无用户绑定记录
            </Text>
          ) : (
            <>
              <TextInput
                size="xs"
                placeholder="搜索 tgUserId / platformAccount / childSiteCode"
                value={bindingSearch}
                onChange={(e) => setBindingSearch(e.currentTarget.value)}
              />
              <AdminTable minWidth={600}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>TG 用户 ID</Table.Th>
                    <Table.Th>平台账号</Table.Th>
                    <Table.Th>子站</Table.Th>
                    <Table.Th>实例 ID</Table.Th>
                    <Table.Th>绑定时间</Table.Th>
                    <Table.Th>操作</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {pagedBindings.length === 0 ? (
                    <Table.Tr>
                      <Table.Td colSpan={6}>
                        <Text c="dimmed" size="sm" ta="center">
                          无匹配绑定
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ) : null}
                  {pagedBindings.map((b) => (
                    <Table.Tr key={`${b.tgUserId}-${b.platformAccount}`}>
                      <Table.Td>{b.tgUserId}</Table.Td>
                      <Table.Td>{b.platformAccount}</Table.Td>
                      <Table.Td>
                        <Badge variant="light" size="sm">
                          {b.childSiteCode}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs">{b.paymentInstanceId}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs">{new Date(b.boundAt).toLocaleString()}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Button
                          size="xs"
                          color="red"
                          variant="light"
                          onClick={() => confirmUnbind(b)}
                        >
                          解绑
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </AdminTable>
              <TablePagination
                page={bindingsPage}
                pageSize={bindingsPageSize}
                total={bindingsTotal}
                totalPages={bindingsTotalPages}
                onPageChange={setBindingsPage}
              />
            </>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
